import type {
  PaginatedResponse,
  PaginatedMeta,
} from '@dc-shared-web/types/pagination'

export interface CreateComparisonParams {
  doc_guid_a: string
  doc_guid_b: string
  crunch_uuid: string
  user_uuid: string
  generate_new_analysis: boolean
}
export interface ComparisonStatusParams {
  version_a_guid: string
  version_b_guid: string
}
export interface VCCardProps {
  uuid: string
  version_a_name: string
  version_a_guid: string
  version_b_name: string
  version_b_guid: string
  created_on: string
  crunched_by: string
  total_changes: number
  status: ComparisonStatus
}

export interface ComparisonListResponse {
  data: VCCardProps[]
}

interface Bounds {
  height: number
  left: number
  top: number
  width: number
}

export enum Version {
  A = 'a',
  B = 'b',
}

export interface SourceBounds {
  bounds: Bounds
  operation: 'add' | 'delete' | 'unchanged'
  page: number
  text: string
  version: Version | undefined
}

export interface Source {
  id: number
  lines: ReadonlyArray<SourceBounds>
  start_page?: number
  end_page?: number
  total_lines?: number
  has_many_lines?: boolean
}

export interface VersionChanges {
  analysis: string
  analysis_title: string
  change_type: 'edit' | 'delete' | 'add'
  sources: ReadonlyArray<Source>
}

export interface VersionData {
  doc_guid: string
  doc_name: string
}
export interface FetchComparisonDataParams {
  version_a_guid: string
  version_b_guid: string
  page?: string
  limit?: string
}
export interface ComparisonData {
  version_a: VersionData
  version_b: VersionData
  changes: Change[]
  created_on: string | null
  crunched_by: string | null
  status: ComparisonStatus
}

export type ComparisonDataResponse = PaginatedResponse<
  ComparisonData,
  PaginatedMeta
>

export interface Change {
  uuid: string
  analysis_title: string
  analysis: string
  change_type: 'edit' | 'add' | 'delete'
  sources: ReadonlyArray<Source>
  is_resolved: boolean
  notes: Note[]
}

export interface Note {
  uuid: string
  diff_id: number
  content: string
  created_by: string
  created_at: string
  updated_at: string
  updated_by: string
  deleted_at: string | null
}

export interface VersionView {
  docName: string
  pages: number[]
  version: Version
  isSelected?: boolean
  onClick?: () => void
}

export interface ComparisonHighlightOverlay {
  id: string
  pageIndex: number
  pageHeight: number
  pageWidth: number
  position: { x: number; y: number }
  bounds: Bounds
}

export interface ComparisonSummaryProps {
  id: number
  uuid: string
  title: string
  status: 'resolved' | 'pending'
  type: 'edit' | 'add' | 'delete' | 'move'
  content: string[]
  citations: {
    id: number
    document: string
    documentId: string
    page: number
    lines: readonly SourceBounds[]
    hasManyLines?: boolean
    startPage?: number
    endPage?: number
    totalLines?: number
  }[]
  notes: Note[]
}

export type CitationOverlayItem = {
  id: string
  baseId: string // Base ID for grouping related overlays across pages
  pageIndex: number
  position: { x: number; y: number }
  type: 'edit' | 'add' | 'delete'
  source: Source
  version: Version
  changeIndex: number // Index of the change in the full list for navigation purposes
}

export enum ComparisonCode {
  NEW_COMPARISON = 'NEW_COMPARISON',
  EXISTING_COMPARISON = 'EXISTING_COMPARISON',
  REGENERATE_COMPARISON = 'REGENERATE_COMPARISON',
  PROCESSING_COMPARISON = 'PROCESSING_COMPARISON',
  RETRY_COMPARISON = 'RETRY_COMPARISON',
  QUEUED_COMPARISON = 'QUEUED_COMPARISON',
}

export enum ComparisonStatus {
  PROCESSING = 'processing',
  QUEUED = 'queued',
  DONE = 'done',
  FAILED = 'failed',
}

export interface CompareStatusResponse {
  status: ComparisonStatus
  code: ComparisonCode
  description: string
}
